import React from "react";

interface PlateNumberHistoryContextType {
  plateNumberSearchHistory: string[];
  addPlateNumberToHistory: (entry: string) => void;
  removePlateNumberFromHistory: (entry: string) => void;
}

const PlateNumberHistoryContext = React.createContext<PlateNumberHistoryContextType | undefined>(undefined);

export const PlateNumberHistoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [plateNumberSearchHistory, setPlateNumberSearchHistory] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const historyJson = window.localStorage.getItem("plateNumberSearchHistory");
    if (!historyJson) {
      return;
    }

    try {
      const parsed = JSON.parse(historyJson);
      if (Array.isArray(parsed)) {
        setPlateNumberSearchHistory(parsed);
      }
    } catch {
      // Ignore malformed data
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem("plateNumberSearchHistory", JSON.stringify(plateNumberSearchHistory));
  }, [plateNumberSearchHistory]);

  const addPlateNumberToHistory = React.useCallback((entry: string) => {
    if (!entry) {
      return;
    }

    setPlateNumberSearchHistory((prevHistory) => {
      const nextHistory = [entry, ...prevHistory.filter((item) => item !== entry)].slice(0, 5);
      return nextHistory;
    });
  }, []);

  const removePlateNumberFromHistory = React.useCallback((entry: string) => {
    setPlateNumberSearchHistory((prevHistory) => prevHistory.filter((item) => item !== entry));
  }, []);

  const value = React.useMemo(
    () => ({
      plateNumberSearchHistory,
      addPlateNumberToHistory,
      removePlateNumberFromHistory,
    }),
    [plateNumberSearchHistory, addPlateNumberToHistory, removePlateNumberFromHistory],
  );

  return <PlateNumberHistoryContext.Provider value={value}>{children}</PlateNumberHistoryContext.Provider>;
};

export const usePlateNumberHistory = (): PlateNumberHistoryContextType => {
  const ctx = React.useContext(PlateNumberHistoryContext);
  if (!ctx) throw new Error("usePlateNumberHistory must be used inside PlateNumberHistoryProvider");
  return ctx;
};
